import { Component, Inject, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatIcon, MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { LookUpService } from '../../../services/lookupservice';
import { DropdownOption } from '../../../interfaces/feedstocklistmodel';
import { FeedstockService } from '../../../services/feedstockservice';
import { GeneratorsList, GetMarketGeneratorsResponse } from '../../../interfaces/generatorsmodel';
import { StreamsList } from '../../../interfaces/streamsmodel';
import { SchedulingSitesResponse } from '../../../interfaces/siteType';
import { IdName } from '../../../interfaces/pricematrixmodel';
import { numericInputLimit } from '../../shared/commonutils/helper';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { SelectSearchComponent } from "../../shared/components/select-search/select-search.component";
import { SaveAttemptErrorMatcher } from '../../shared/commonutils/errorStateDirective';
import { MatSelectItems } from '../../../interfaces/lookup';

@Component({
  selector: 'app-haulerPricing-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    MatButtonModule,
    MatCardModule,
    MatInputModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatSelectModule,
    MatTableModule,
    MatMenuModule,
    MatDialogModule,
    MatIconModule,
    DragDropModule,
    SelectSearchComponent
],
  templateUrl: './hauler-pricing-dialog.component.html',
  styleUrls: ['./hauler-pricing-dialog.component.scss'],
})
export class HaulerPricingDialogComponent {
  disposalSites = signal<SchedulingSitesResponse[]>([]);
  filteredDisposalSites = signal<SchedulingSitesResponse[]>([]);
  canSave: boolean = false;
  constructor(
    private lookUp: LookUpService,
    private feedstockService: FeedstockService,
    public dialogRef: MatDialogRef<HaulerPricingDialogComponent, any>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private fb: FormBuilder
  ) {}
  haulerPricingForm!: FormGroup;
  isEditMode = false;
  haulerPricingFormData: any;
  haulerData: any;
  cacheID = '';
  unitType: IdName[] = [];
  generators: GetMarketGeneratorsResponse[] = [];
  filteredGenerators: GetMarketGeneratorsResponse[] = [];
  selectCustomerItems: MatSelectItems[] = [];
  selectStreamItems: MatSelectItems[] = [];
  streams: StreamsList[] = [];
  filteredStreams: StreamsList[] = [];
  attemptSave = signal(false);
  public matcher = new SaveAttemptErrorMatcher(() => this.attemptSave());
  uomName: string = '';
  generatorName: string | null = '';
  streamName: string | null = '';

  formatCurrencyPrice(event: Event): void {
    const input = event.target as HTMLInputElement;
    let value = input.value.replace(/[^0-9.]/g, '');
    const parts = value.split('.');

    if (parts.length > 2) {
      value = parts[0] + '.' + parts[1];
    }

    if (parts[1]?.length > 4) {
      value = parts[0] + '.' + parts[1].substring(0, 4);
    }

    this.haulerPricingForm.get('price')?.setValue(value);
  }

  formatCurrencyFuelSurcharge(event: Event): void {
    const input = event.target as HTMLInputElement;
    let value = input.value.replace(/[^0-9.]/g, '');
    const parts = value.split('.');

    if (parts.length > 2) {
      value = parts[0] + '.' + parts[1];
    }

    if (parts[1]?.length > 4) {
      value = parts[0] + '.' + parts[1].substring(0, 4);
    }

    this.haulerPricingForm.get('fuel_Surcharge')?.setValue(value);
  }

  formatCurrencyDemurrage(event: Event): void {
    const input = event.target as HTMLInputElement;
    let value = input.value.replace(/[^0-9.]/g, '');
    const parts = value.split('.');

    if (parts.length > 2) {
      value = parts[0] + '.' + parts[1];
    }

    if (parts[1]?.length > 4) {
      value = parts[0] + '.' + parts[1].substring(0, 4);
    }

    this.haulerPricingForm.get('demurag_Rate')?.setValue(value);
  }

  ngOnInit() {
    this.loadDropdownData();
    console.log('Received data in dialog:', this.data);
    this.canSave = this.data.canSave;
    this.isEditMode = !this.data?.isAddForm;
    this.cacheID = this.data?.formData?.cacheID || '';
    if (this.data?.formData?.uom) {
      this.findUnitTypeNameById(this.data?.formData?.uom);
    }
    this.buildForm();
  }

  loadDropdownData() {
    // this.getUnitsOfmeasureData();
    this.getGeneratorsByMarketId('-1');
    
    this.feedstockService.getDisposalSitesForHaulerPricing().subscribe({
      next: async (response) => {
        console.log("disposal sites response", response);
        this.disposalSites.set(response);
        this.filteredDisposalSites.set(response);

        if(this.data?.formData?.disposalSite) {
          const selectedSiteIds = this.data.formData.disposalSite;
          const selectedSites = response.filter(site => selectedSiteIds.includes(site.siteName));
          this.haulerPricingForm.get('disposalSite')?.setValue(selectedSites.map(site => site.sid));
        }
      }
    });
    
    this.lookUp.getSampleFrequencyLookup().subscribe({
      next: async (response) => {
        this.unitType = response.filter(item => item.type === 'HP').map(item => ({ id: item.id, name: item.name }));        
      }
    })
  }

  getGeneratorsByMarketId(marketId: string) {
    this.feedstockService.getmarketGenerators(parseInt(marketId), false).subscribe({
      next: async (response) => {
        this.generators = response;
        this.filteredGenerators = response;
        this.selectCustomerItems = this.generators.map( item => {
          return {
            id: item.id,
            name: item.customerName
          }
        })
        //console.log(this.generators);
        if (this.data?.formData?.generatorId) {
          this.onGeneratorSelectionChange(this.data?.formData?.generatorId);
        }
      },
    });
  }

  onGeneratorSelectionChange(selectedId: number): void {
    if (!selectedId) {
      this.streams = [];
      this.filteredStreams = [];
      return;
    }
    this.getStreamsBygeneratorId(`${selectedId}`, '-1');
    this.findGeneratorNameById(selectedId);
  }

  getStreamsBygeneratorId(genId: string, marketid: string) {
    this.feedstockService.getStreamList(marketid, genId).subscribe({
      next: async (response) => {
        this.streams = response;
        this.filteredStreams = response;
        this.selectStreamItems = this.streams.map( item=> {
          return{
            id: item.streamId,
            name: item.streamName
          }
        })
        //console.log(this.streams);
        if (this.data?.formData?.streamId) {
          this.findStreamNameById(this.data?.formData?.streamId);
        }
      },
    });
  }

  onCustomerReceived(event: number| null){
    this.haulerPricingForm.get('generatorId')?.setValue(event ?? null);
    this.haulerPricingForm.get('generatorId')?.markAsTouched();

    // reset stream when customer changes
    this.haulerPricingForm.get('streamId')?.setValue(null);
    this.selectStreamItems = [];
    this.streams = [];

    if (event) {
      this.onGeneratorSelectionChange(event);
    }
  }

  onStreamReceived(event: number | null){
    this.haulerPricingForm.get('streamId')?.setValue(event ?? null);
    this.haulerPricingForm.get('streamId')?.markAsTouched();

    if (event) {
      this.findStreamNameById(event);
    }
  }

  onCancel(): void {
    this.dialogRef.close({ submitted: false });
  }

  onSaveClick(): void {
    this.dialogRef.close({ submitted: true, data: this.haulerPricingFormData });
  }
  
  onSubmit(): void {    
    this.attemptSave.set(true);
    if (this.haulerPricingForm.valid) {
      const formData = { ...this.haulerPricingForm.value };
      var siteIds = this.haulerPricingForm.value.disposalSite;
      var siteNames = this.disposalSites().filter(site => siteIds.includes(site.sid)).map(site => site.siteName);
      formData['disposalSiteids'] = siteIds.join(',');
      formData['disposalSite'] = siteNames;
      formData['id'] = this.data?.formData?.id || this.data.id || 0;
      formData['cacheID'] = this.cacheID || 0;
      formData['uomName'] = this.uomName || '';
      formData['generatorName'] = this.generatorName || '';
      formData['streamName'] = this.streamName || '';
      formData['haulerID'] = this.data?.formData?.haulerID || 0;
      //formData['disposalSite'] = this.haulerPricingForm.value.disposalSite || [];
      this.dialogRef.close({ submitted: true, data: formData });
    }
  }

  buildForm(): void {
    const ds = this.data?.formData?.disposalSite || [];
    const dsArray = typeof ds === 'string' ? ds.split(',') : (ds || [ ]);
    this.haulerPricingForm = this.fb.group({
      uom: [this.data?.formData?.uom, Validators.required],
      price: [
        this.data?.formData?.price,
        [Validators.required, Validators.pattern(/^\d+(\.\d{1,4})?$/)],
      ],
      fuel_Surcharge: [
        this.data?.formData?.fuel_Surcharge,
        [Validators.required, Validators.pattern(/^\d+(\.\d{1,4})?$/)],
      ],
      demurag_Rate: [
        this.data?.formData?.demurag_Rate,
        [Validators.required, Validators.pattern(/^\d+(\.\d{1,4})?$/)],
      ],
      disposalSite: [dsArray, Validators.required],
      generatorId: [this.data?.formData?.generatorId || null, Validators.required],
      streamId: [this.data?.formData?.streamId || null, Validators.required],
    });
  }

  findUnitTypeNameById(id: number): void {
    const unit = this.unitType.find((item) => item.id === id);
    this.uomName = unit ? unit.name : '';
  }
  findGeneratorNameById(id: number): void {
    const generator = this.generators.find((item) => item.id === id);
    this.generatorName = generator?.customerName || '';
  }
  findStreamNameById(id: number): void {
    const stream = this.streams.find((item) => item.streamId === id);
    this.streamName = stream ? stream.streamName : '';
  }

  filterDisposalSite(event: Event): void {
    const searchTerm = (event.target as HTMLInputElement).value.toLowerCase();
    const selectedIds = this.haulerPricingForm.get('disposalSite')?.value || [];
    if (!searchTerm) {
      this.filteredDisposalSites.set([...this.disposalSites()]);
    } else {
      const filtered = this.disposalSites().filter(
        g => g.siteName.toLowerCase().includes(searchTerm) || selectedIds.includes(g.sid)
      );
      this.filteredDisposalSites.set(filtered);
    }
  }

  filterCustomer(event: Event): void {
    const searchTerm = (event.target as HTMLInputElement).value.toLowerCase();
    const selectedId = this.haulerPricingForm.get('generatorId')?.value;
    if (!searchTerm) {
      this.filteredGenerators = [...this.generators];
    } else {
      const filtered = this.generators.filter(
        g => g.customerName.toLowerCase().includes(searchTerm)
      );
      this.filteredGenerators = filtered;
    }
  }

  filterStream(event: Event): void {
    const searchTerm = (event.target as HTMLInputElement).value.toLowerCase();
    const selectedId = this.haulerPricingForm.get('streamId')?.value;
    if (!searchTerm) {
      this.filteredStreams = [...this.streams];
    } else {
      const filtered = this.streams.filter(
        s => s.streamName.toLowerCase().includes(searchTerm)
      );
      this.filteredStreams = filtered;
    }
  }

  roundOff(event: Event, maxValue: number, maxDecimals: number, type: number, controlName: string): void {
    const input = event.target as HTMLInputElement;
    let val = input.value;
    
    val = numericInputLimit(event, maxValue, maxDecimals);
    
    //this.displayValue.set(val);
    input.value = val;

    // 6. Parsing for the backing variables
    const isValueEmpty = val === '' || val === '.';
    if (isValueEmpty) {
      this.haulerPricingForm.get(controlName)?.setValue(null, { emitEvent: false });
    } else if (val.endsWith('.')) {
      this.haulerPricingForm.get(controlName)?.setErrors({ 'incompleteDecimal': true });
    } else {      
      const parsedValue = parseFloat(val);
      this.haulerPricingForm.get(controlName)?.setValue(parsedValue, { emitEvent: false });
    }

    requestAnimationFrame(() => {
      if (document.activeElement === input) {
        input.value = val;   // put "14.10" back so next keystroke appends correctly
      }
    });
  }
}
